import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../../../../components/ui/Table';
import Button from '../../../../components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from '../../../../components/ui/Dialog';
import { Spinner } from '../../../../components/ui/Spinner';
import AppIcon from '../../../../components/AppIcon';
import Pagination from '../../../../components/Pagination';
import { PageLayout } from '../../../../components/ui/PageLayout';
import { Gift } from 'lucide-react';
import { useToast, ToastRenderer } from '../../../../hooks/useToast';
import { CompanyBenefitTypeView, CompanyBenefitTypeInput } from '../../../../models/payroll';
import {
  getCompanyBenefitTypesWithStats,
  deleteCompanyBenefitType,
  editCompanyBenefitType,
  addCompanyBenefitType,
} from '../../../../api/Payroll/benefit';
import { formatNumber } from '../../../../utils';
import BenefitTypeForm from './benefitTypeForm';

const PAGE_SIZE = 10;

export default function BenefitsSettings() {
  const { t } = useTranslation();
  const { toast, showToast, dismissToast } = useToast();

  const [benefits, setBenefits] = useState<CompanyBenefitTypeView[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedPageIndex, setSelectedPageIndex] = useState(0);

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [benefitTypeId, setBenefitTypeId] = useState(0);
  const [selectedBenefitName, setSelectedBenefitName] = useState('');
  const [currentBenefitType, setCurrentBenefitType] = useState<CompanyBenefitTypeInput | undefined>();

  const offset = selectedPageIndex * PAGE_SIZE;

  const fetchBenefitTypes = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getCompanyBenefitTypesWithStats();
      console.log('Benefits', result.payload);
      setBenefits(result.payload);
    } catch (error: any) {
      console.log('Error', error);
      if (error.errors && error.errors.length > 0) {
        error.errors.forEach((err: any) => {
          if (err.description) showToast(err.description, 'error');
        });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBenefitTypes();
  }, [fetchBenefitTypes]);

  const onPageChange = (newPage: number) => {
    setSelectedPageIndex(newPage - 1);
  };

  /* ── Add ── */
  const handleAddBenefit = async (input: CompanyBenefitTypeInput) => {
    try {
      setLoading(true);
      await addCompanyBenefitType(input);
      showToast(String(t('payroll:Benefit Type Added')), 'success');
    } catch (error: any) {
      console.log('Error adding benefit:', error);
      if (error.errors && error.errors.length > 0) {
        error.errors.forEach((err: any) => {
          if (err.description) showToast(err.description, 'error');
        });
      }
      if (!error.errors) showToast(String(t('payroll:Failed to add benefit type')), 'error');
    } finally {
      setLoading(false);
    }
    setAddDialogOpen(false);
    await fetchBenefitTypes();
  };

  /* ── Edit ── */
  const handleClickOpenEditDialog = (benefit: CompanyBenefitTypeView) => {
    setCurrentBenefitType({
      id: benefit.id,
      name: benefit.name,
      description: benefit.description,
      cost: benefit.cost,
      price: benefit.price,
      visibleToEmployees: benefit.visibleToEmployees,
    });
    setEditDialogOpen(true);
  };

  const handleEditBenefit = async (input: CompanyBenefitTypeInput) => {
    try {
      setLoading(true);
      await editCompanyBenefitType(input.id, input);
      showToast(String(t('payroll:Benefit Type Updated')), 'success');
    } catch (error: any) {
      console.log('Error editing benefit:', error);
      if (error.errors && error.errors.length > 0) {
        error.errors.forEach((err: any) => {
          if (err.description) showToast(err.description, 'error');
        });
      }
      if (!error.errors) showToast(String(t('payroll:Failed to update benefit type')), 'error');
    } finally {
      setLoading(false);
    }
    setEditDialogOpen(false);
    await fetchBenefitTypes();
  };

  /* ── Delete ── */
  const handleClickOpenDeleteDialog = (benefitName: string, id: number) => {
    setDeleteDialogOpen(true);
    setSelectedBenefitName(benefitName);
    setBenefitTypeId(id);
  };

  const handleClickCloseDeleteDialog = async () => {
    setDeleteDialogOpen(false);
    await fetchBenefitTypes();
  };

  const deleteBenefitType = async (id: number) => {
    try {
      setLoading(true);
      await deleteCompanyBenefitType(id);
      fetchBenefitTypes();
      setDeleteDialogOpen(false);
      showToast(String(t('payroll:Benefit Type Deleted')), 'success');
    } catch (error) {
      console.log('Error deleting benefit:', error);
      showToast(String(t('payroll:Failed to delete benefit type')), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseBenefitForm = () => {
    setAddDialogOpen(false);
    setEditDialogOpen(false);
  };

  const totalPages = Math.ceil(benefits.length / PAGE_SIZE);
  const currentPage = selectedPageIndex + 1;

  const visibleBenefits = benefits.slice(offset, Math.min(offset + PAGE_SIZE, benefits.length));

  return (
    <PageLayout
      title={t('payroll:Benefit Types')}
      icon={Gift}
      accentColor="emerald"
      headerActions={(
        <Button variant="outline" iconName="PlusCircle" onClick={() => setAddDialogOpen(true)} data-testid="add-type-btn">
          {t('payroll:Add Benefit Type')}
        </Button>
      )}
    >
      <div className="flex flex-col flex-1 gap-4">
      {/* Table */}
      <div className="relative rounded-lg border border-border bg-card">
        {loading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-card/60">
            <Spinner size="lg" />
          </div>
        )}

        <Table data-testid="settings-table">
          <TableHeader>
            <TableRow>
              <TableHead>{t('payroll:Benefit Type')}</TableHead>
              <TableHead>{t('payroll:Description')}</TableHead>
              <TableHead>{t('payroll:Cost')}</TableHead>
              <TableHead>{t('payroll:Price')}</TableHead>
              <TableHead>{t('payroll:Visible To Employees')}</TableHead>
              <TableHead>{t('payroll:Number Of Beneficiaries')}</TableHead>
              <TableHead>{t('payroll:Total Cost')}</TableHead>
              <TableHead>{t('payroll:Total Price')}</TableHead>
              <TableHead>{t('common:Actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {benefits.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={9}>
                  <div className="flex flex-col items-center gap-2 py-8 text-center text-muted-foreground">
                    <span className="font-medium">{t('payroll:No benefit types found')}</span>
                    <span className="text-sm opacity-80">{t('payroll:Click Add Benefit Type to create one')}</span>
                  </div>
                </TableCell>
              </TableRow>
            )}
            {visibleBenefits.map((row: CompanyBenefitTypeView) => (
              <TableRow key={row.id}>
                <TableCell className="font-semibold">{row.name}</TableCell>
                <TableCell>{row.description}</TableCell>
                <TableCell>{`${formatNumber(row.cost ?? 0)} ${row.currency?.currencyCode}`}</TableCell>
                <TableCell>{`${formatNumber(row.price ?? 0)} ${row.currency?.currencyCode}`}</TableCell>
                <TableCell>{row.visibleToEmployees ? t('common:Yes') : t('common:No')}</TableCell>
                <TableCell>{row.numberOfBeneficiaries}</TableCell>
                <TableCell className="font-semibold">{`${formatNumber(row.totalCost)} ${row.currency?.currencyCode}`}</TableCell>
                <TableCell className="font-semibold">{`${formatNumber(row.totalPrice)} ${row.currency?.currencyCode}`}</TableCell>
                <TableCell className="w-[100px]">
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={String(t('common:Edit'))}
                      title={String(t('common:Edit'))}
                      onClick={() => handleClickOpenEditDialog(row)}
                    >
                      <AppIcon name="Pencil" size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={String(t('common:Delete'))}
                      title={String(t('common:Delete'))}
                      onClick={() => handleClickOpenDeleteDialog(row.name, row.id)}
                    >
                      <AppIcon name="Trash2" size={16} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages || 1}
        onPageChange={onPageChange}
      />

      {/* Add / Edit form dialog */}
      <BenefitTypeForm
        open={addDialogOpen || editDialogOpen}
        onClose={handleCloseBenefitForm}
        onSave={addDialogOpen ? handleAddBenefit : handleEditBenefit}
        benefitType={currentBenefitType}
      />

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(open) => { if (!open) handleClickCloseDeleteDialog(); }}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>{t('payroll:Delete Benefit Type')}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-foreground">
              {t('payroll:Confirm delete benefit type')} <strong>{selectedBenefitName}</strong>?
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={handleClickCloseDeleteDialog}>
              {t('common:Cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteBenefitType(benefitTypeId)}
            >
              {t('common:Delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ToastRenderer toast={toast} onDismiss={dismissToast} />
      </div>
    </PageLayout>
  );
}
